<template>
  <div class="px-2">
    <!-- Check box -->
    <div class="">
      <UserSelectBox v-if="!isCurrentUser && !isDeleted" v-bind:user="item" data-cy="list-users-selectBox" />
      <span v-else />
    </div>

    <!-- User names -->
    <div v-bind:class="getClass()" data-cy="list-users-names">{{ item.name }}</div>

    <!-- Email address -->
    <div v-bind:class="getClass()" data-cy="list-users-email"><v-icon v-if="item.emailbounce" size="small" color="primary">mdi-email-off-outline</v-icon>{{ item.email }}</div>

    <!-- User type @ Root Node -->
    <div v-bind:class="getClass()" data-cy="list-users-userType">
      <div v-if="userTypeEndpoints.length === 0" />
      <RouterLink
        v-else-if="userTypeEndpoints.length === 1"
        :to="{ name: 'node:view', params: { id: userTypeEndpoints[0].id } }"
        target="_blank"
        class="px-2"
      >
        {{ userTypeEndpoints[0].name }} ({{ userTypeEndpoints[0].type }})
      </RouterLink>
      <VSelect
        v-else
        :placeholder="$t('routes.usage.rootFoldersCount', userTypeEndpoints.length)"
        :items="userTypeEndpoints.map((val) => ({ id: val.id, title: `${val.name} (${val.type})` }))"
        item-title="title"
        item-value="id"
        @update:model-value="openAccessPointInNewTab"
        variant="solo-filled"
        flat
        density="compact"
        hide-details
      />

      <VTooltip
        v-if="item.mfaEnabled"
        location="bottom"
      >
        <template v-slot:activator="{ props }">
          <VIcon v-bind="props" color="primary">mdi-two-factor-authentication</VIcon>
        </template>

        <span>{{ $t("routes.users.mfaEnabled") }}</span>
      </VTooltip>
    </div>

    <!-- Created At -->
    <div v-bind:class="getClass()" data-cy="list-users-createdAt">{{ isoToDatetime(item.createdat) }}</div>

    <div v-if="!isCurrentUser && !isDeleted" data-cy="list-users-actionsColumn">
      <v-menu
        v-if="$canControlUser()"
        v-model="showActionMenu"
        location="left bottom"
        :persistent="false"
        close-on-content-click
      >
        <template v-slot:activator="{ props }">
          <VBtn
            v-bind="props"
            icon="mdi-dots-vertical"
            width="32"
            height="32"
            rounded="circle"
            color="primary"
            variant="text"
            density="comfortable"
            data-cy="list-users-actions-btn"
          />
        </template>

        <VList width="250px" data-cy="list-users-actions-list">
          <VListItem
            v-on:click="gotoPermissions(item)"
            data-cy="list-users-actions-setPermissions"
          >
            <template v-slot:prepend>
              <VIcon size="small">mdi-folder-key</VIcon>
            </template>
            <template v-slot:title>
              {{ $t('routes.users.foldersAndPermissions') }}
            </template>
          </VListItem>

          <VListItem
          @click="openDeleteUserDialog()"
          data-cy="list-users-actions-deleteUser"
          >
            <template v-slot:prepend>
              <VIcon size="small">mdi-account-off-outline</VIcon>
            </template>

            <template v-slot:title>
              {{ $t('routes.users.deleteUser') }}
            </template>
          </VListItem>


          <VListItem
            v-if="canChangeUserPassword"
            @click="openUpdatePasswordDialog()"
            data-cy="list-users-actions-updateUserPassword"
          >
            <template v-slot:prepend>
              <VIcon size="small">mdi-form-textbox-password</VIcon>
            </template>
            <template v-slot:title>
              {{ $t('routes.users.changePassword') }}
            </template>
          </VListItem>

        </VList>
      </v-menu>

      <DeleteUsersDialog
        v-bind:users="Array(item)"
        v-model="showingDeleteUserDialog"
        @usersDeleted="afterUsersDeleted"
      />

      <UpdatePasswordDialog
        v-if="canChangeUserPassword"
        v-model="showingUpdatePasswordDialog"
        :getEditUser="item"
      />
    </div>
  </div>
</template>

<script>
import { useAuthStore } from '@/stores/AuthStore.js'
import { useUserStore } from '@/stores/UserStore.js'
import UserSelectBox from "./UserSelectBox.vue"
import DeleteUsersDialog from "./dialogs/DeleteUsersDialog.vue"
import UpdatePasswordDialog from "./dialogs/UpdatePasswordDialog.vue"
import { isoToDatetime } from '@/components/formatters.js';
import { createMongoAbility } from '@casl/ability';

export default {
  name: "UserListItem",
  components: {
    UserSelectBox,
    DeleteUsersDialog,
    UpdatePasswordDialog,
  },
  props: {
    item: {
      type: Object,
      required: true,
    },
    node: {
      type: Object,
    },
    sortBy: {
      type: String,
      required: false,
    },
    sortDesc: {
      type: Boolean,
      required: false,
      default: false,
    }
  },
  data: function () {
    return {
      showActionMenu: false,

      showingDeleteUserDialog: false,
      showingUpdatePasswordDialog: false,
    };
  },
  methods: {
    isoToDatetime,
    hideActionMenu: function () {
      this.showActionMenu = false;
    },
    openDeleteUserDialog() {
      this.hideActionMenu();
      this.showingDeleteUserDialog = true;
    },
    openUpdatePasswordDialog() {
      this.hideActionMenu();
      this.showingUpdatePasswordDialog = true;
    },
    gotoPermissions(user) {
      const userStore = useUserStore();
      userStore.setEditUser(user);

      this.$router.push('/console/users/permissions');
    },
    getClass: function() {
      return {
        'user-deleted': this.isDeleted,
      };
    },
    afterUsersDeleted: function() {
      const successMessage = this.$t('routes.users.usersDeleted', 1);
      this.$showMessage(successMessage);

      const userStore = useUserStore();
      userStore.resetSelectedUsers();
      userStore.refreshUsers({ accessPointId: this.activeAccessPointId });
    },
    checkForOneMainTenant: function(user) {
      if (user.tenants && Array.isArray(user.tenants) && user.tenants.length === 1) {
        const tenantId = user.tenants[0].id;

        if (this.activeTenantId === tenantId) {
          return true;
        }
      }

      return false;
    },
    openAccessPointInNewTab: function(id) {
      const resolvedRoute = this.$router.resolve({
        name: 'node:view',
        params: { id }
      });

      window.open(resolvedRoute.href, '_blank');
    },
  },
  computed: {
    userTypeEndpoints: function () {
      const userTypes = [];
      const translatedUserTypes = {
        'teamMember': this.$t(`routes.users.teamMember`),
        'client': this.$t(`routes.users.clientUser`),
      };

      if (!this.item.tenants || this.item.tenants.length === 0) {
        return [];
      }

      let userAccessPointAbilities;

      for (const tenant of this.item.tenants) {
        for (const accessPoint of tenant.accessPoints) {
          userAccessPointAbilities = createMongoAbility(accessPoint.group.abilities);
          userTypes.push({
            id: accessPoint.node.id,
            name: accessPoint.node.name,
            type: translatedUserTypes[
              userAccessPointAbilities.can('belongsto', 'Team') ? 'teamMember' : 'client'
            ] + (accessPoint.group.expireat ? ' 🕑' : ''),
          });
        }
      }

      const sortModifier = this.sortBy === 'rootNodes' && this.sortDesc ? -1 : 1;
      return userTypes.sort((a, b) => a.name.localeCompare(b.name) * sortModifier);
    },

    isCurrentUser: function() {
      const authStore = useAuthStore();
      const dbUser = authStore.dbUser;

      return dbUser && (dbUser.id == this.item.id);
    },
    isDeleted: function() {
      return this.item.deletedAt !== null;
    },
    accessPointId: function() {
      if (Array.isArray(this.item.tenants)) {
        const firstAccessPoint = this.item.tenants[0]?.accessPoints[0];
        return firstAccessPoint?.node?.id || null;
      }

      return null;
    },
    activeAccessPointId: function() {
      const authStore = useAuthStore();
      return authStore.rootNodeId;
    },
    activeTenantId: function() {
      const authStore = useAuthStore();
      return authStore.getActiveTenantId;
    },
    userType: function() {
      const activeTenantId = this.activeTenantId;

      const activeTenant = this.item.tenants.find((tenant) => tenant.id === activeTenantId);
      return activeTenant?.userType || null;
    },
    canChangeUserPassword: function () {
      const hasOneMainTenant = this.checkForOneMainTenant(this.item);
      return this.$canControlUser() && hasOneMainTenant;
    }
  },
};
</script>

<style>
.user-deleted {
  text-decoration: line-through;
  color: #999;
}

.user-endpoints-dropdown-btn {
  text-transform: none !important;
}
</style>
